import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { FiChevronDown, FiMenu } from "react-icons/fi";
import media from "../../styles/media";
import Container from "../ui/Container";
import { MAIN_NAV } from "../../config/navigation";

const Bar = styled.nav`
  background-color: ${({ theme }) => theme.colors.primary};
`;

const Inner = styled.div`
  display: flex;
  align-items: center;
  min-height: ${({ theme }) => theme.layout.headerHeight};
`;

const ItemList = styled.ul`
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  align-items: stretch;
  list-style: none;

  ${media.md} {
    display: none;
  }
`;

const Item = styled.li`
  position: relative;

  /* Подменю открывается и по наведению, и по фокусу с клавиатуры. */
  &:hover > ul,
  &:focus-within > ul {
    visibility: visible;
    opacity: 1;
    transform: translateY(0);
  }
`;

const itemStyles = `
  display: flex;
  align-items: center;
  gap: 6px;
  height: 100%;
  padding: 16px 18px;
  color: #fff;
  font-size: 15px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.25s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.14);
    text-decoration: none;
  }
`;

const ItemLink = styled(NavLink)`
  ${itemStyles}

  &.active {
    background-color: rgba(0, 0, 0, 0.14);
  }
`;

const SubMenu = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  z-index: ${({ theme }) => theme.zIndex.dropdown};
  min-width: 200px;
  padding: 6px 0;
  list-style: none;
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: 0 0 ${({ theme }) => theme.radii.sm}
    ${({ theme }) => theme.radii.sm};
  box-shadow: ${({ theme }) => theme.shadows.menu};
  visibility: hidden;
  opacity: 0;
  transform: translateY(-6px);
  transition:
    opacity 0.2s ease,
    transform 0.2s ease,
    visibility 0.2s;
`;

const SubMenuLink = styled(NavLink)`
  display: block;
  padding: 10px 18px;
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.fontSizes.sm};

  &:hover,
  &.active {
    background-color: ${({ theme }) => theme.colors.primarySoft};
    color: ${({ theme }) => theme.colors.primaryDark};
    text-decoration: none;
  }
`;

const BurgerButton = styled.button`
  display: none;
  margin-left: auto;
  padding: 10px 14px;
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: ${({ theme }) => theme.radii.sm};
  background: transparent;
  color: ${({ theme }) => theme.colors.onPrimary};
  cursor: pointer;

  ${media.md} {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
`;

/**
 * Десктопное меню строится из `MAIN_NAV`. Пункты — это `NavLink`, а не `<h1>`
 * с `onClick`, как было раньше: работает средняя кнопка мыши, открытие в новой
 * вкладке, подсветка активного раздела и навигация с клавиатуры.
 */
export function MainNav({ onOpenMenu, menuButtonRef }) {
  const { t } = useTranslation();

  return (
    <Bar aria-label="Основная навигация">
      <Container>
        <Inner>
          <ItemList>
            {MAIN_NAV.map((item) => (
              <Item key={item.id}>
                <ItemLink to={item.to} end={item.end}>
                  {t(item.labelKey)}
                  {item.children && <FiChevronDown aria-hidden="true" />}
                </ItemLink>

                {item.children && (
                  <SubMenu>
                    {item.children.map((child) => (
                      <li key={child.id}>
                        <SubMenuLink to={child.to}>
                          {t(child.labelKey)}
                        </SubMenuLink>
                      </li>
                    ))}
                  </SubMenu>
                )}
              </Item>
            ))}
          </ItemList>

          <BurgerButton
            type="button"
            ref={menuButtonRef}
            onClick={onOpenMenu}
            aria-haspopup="dialog"
          >
            <FiMenu aria-hidden="true" />
            {t("menu")}
          </BurgerButton>
        </Inner>
      </Container>
    </Bar>
  );
}

export default MainNav;
